import CTNButton from '@ctn/ui/components/ctn-button';
import CTNInput from '@ctn/ui/components/ctn-input';
import { type CTNButtonProps } from '@ctn/ui/types/ctn-button';
import { type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import FieldIcon from '@/components/FieldIcon/FieldIcon';
import { useGlobalContext } from '@/context/global.context';

import { ESTADO_FIRMA_COLOR, ESTADOS_FIRMA } from '@/constants/tramite.constants';
import { getIconDocumento } from '@/helpers/upload';
import { type IProtocolo } from '@/interfaces/model/don.interface';


type ModalParams = {
	readonly protocolo: IProtocolo;
	readonly hasAcciones: boolean;
	readonly onClickVerDetalle?: () => void;
};

const DatosFirma = ({
	protocolo,
	hasAcciones,
	onClickVerDetalle,
}: ModalParams): ReactElement => {
	const {
		control,
		watch,
	} = useForm<IProtocolo>({
		defaultValues: protocolo,
	});
	const {
		maestrosTramite: {  estadoRevisionFirma },
	} = useGlobalContext();

	const copiaElectronica = watch('copiaElectronica');

	const buttonCommonProps: CTNButtonProps = {
		size: 'size-40', 
		mode: 'secondary'
	};

	const hasActionFechaAutorizacion = () => hasAcciones ? 'o-grid__col-4@landscape o-grid__col-2@desktop' : 'o-grid__col-3@landscape';
	const existsSubsanacionFirma = () => protocolo.subsanacionFirma && protocolo.subsanacionFirma?.estado.id !== ESTADOS_FIRMA.CANCELADA;

	return (
		<div className="[ o-grid ] [ u-flow ]">
			<div className="[ o-grid__row ] [ c-pnp-company-data__readonly-row ]">
				<CTNInput
					noFeedBack
					displayOnly
					condensed
					type="text"
					classNameField="o-grid__col-12 o-grid__col-6@portrait o-grid__col-3@landscape o-grid__col-3@desktop"
					name="numero"
					label="NÚMERO DE PROTOCOLO"
					control={control}
				/>	
				<CTNInput
					noFeedBack
					displayOnly
					condensed
					type="text"
					classNameField="o-grid__col-12 o-grid__col-1@portrait o-grid__col-1@landscape"
					name="bis"
					label="BIS"
					control={control}
				/>
				<CTNInput
					noFeedBack
					displayOnly
					condensed
					type="text"
					classNameField={`o-grid__col-12 o-grid__col-6@portrait ${existsSubsanacionFirma() 
						? 'o-grid__col-4@landscape' 
						: hasActionFechaAutorizacion()
					}`}
					name="fechaAutorizacion"
					label="FECHA DE AUTORIZACIÓN"
					control={control}
				/>	
				{existsSubsanacionFirma() && (
					<>
						<CTNInput
							noFeedBack
							displayOnly
							condensed
							type="text"
							classNameField={`o-grid__col-12 o-grid__col-6@portrait ${existsSubsanacionFirma() ? 'o-grid__col-3@landscape' : 'o-grid__col-2@landscape'}`}
							name="subsanacionFirma.fechaCreacion"
							label="FECHA DE PETICIÓN"
							control={control}
						/>
						<div className="o-grid__col-12 o-grid__col-6@portrait o-grid__col-4@landscape">
							<FieldIcon
								className="c-pnp-form-group--flex-col" icon="status" title="ESTADO DE LA REVISIÓN"
								iconColor={ESTADO_FIRMA_COLOR[protocolo.subsanacionFirma?.estado.id ?? 0]}
								text={estadoRevisionFirma.find((it) => it.id === protocolo.subsanacionFirma?.estado.id)?.descripcion ?? ''} 
								tooltip={{ 
									place: 'bottom', 
									content: 'El notario se encargará de revisar la firma. Si tras 6 meses no se obtiene respuesta, la solicitud caducará y podrá solicitarse de nuevo.', 
									maxWidth: 266
								}} 
							/>
						</div>
					</>
				)}
				<div className={`o-grid__col-12 o-grid__col-4@portrait ${existsSubsanacionFirma() 
					? 'o-grid__col-6@landscape o-grid__col-6@desktop' 
					: 'o-grid__col-10@landscape o-grid__col-4@desktop'}
				`}
				>
					<FieldIcon 
						className="c-pnp-form-group--flex-col"
						icon={getIconDocumento(copiaElectronica)} 
						title="DOCUMENTO" 
						text={protocolo.copiaElectronica.nombre} 
					/>
				</div>
				{hasAcciones && (
					<div className={`o-grid__col-12 o-grid__col-2@portrait 
						c-pnp-form-group--flex-end c-pnp-form-group--gap4`}
					>
						<CTNButton {...buttonCommonProps} variant="outline" label="Ver detalle" onClick={onClickVerDetalle} />
					</div>
				)}
			</div>
		</div>
	);
};

export default DatosFirma;
